import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

import { TokenListMetadata, TokenListTag } from "@bridgesplit/abf-sdk";

import { useTokenListQuery } from "../reducers/tokenListApi";
import { useUserTokens } from "./user-tokens";

interface TokenListContextValue {
    principalTokens: TokenListMetadata[] | undefined;
    /** Collateral tokens this wallet should see: wallet-only tokens are included only when held. */
    collateralTokens: TokenListMetadata[] | undefined;
    /** Every collateral token, for flows that configure terms on tokens the wallet doesn't hold. */
    allCollateralTokens: TokenListMetadata[] | undefined;
    collateralTokenMints: string[] | undefined;
    getDecimals: (mint: string | undefined) => number;
    principalTokenMints: string[] | undefined;
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined;
    isPrincipalMint: (mint: string | undefined | null) => boolean;
    isLoading: boolean;
}

const TokenListContext = createContext<TokenListContextValue | undefined>(undefined);

export function TokenListProvider({ children }: { children: ReactNode }) {
    const { data: tokenList, isLoading } = useTokenListQuery();
    const { tokens: userTokens } = useUserTokens();

    const mintToToken = useMemo(() => {
        if (!tokenList) return undefined;
        return new Map(tokenList.map((t) => [t.mint, t]));
    }, [tokenList]);

    const heldMints = useMemo(
        () => new Set(userTokens?.filter((t) => t.tokenAmount.amount !== "0").map((t) => t.mint)),
        [userTokens]
    );
    // Wallet-only tokens (private deal NFTs) only matter to their holders, so they stay out of
    // pickers and explore lists unless this wallet holds one. getMetadata still resolves them.
    const isListed = useCallback(
        (t: TokenListMetadata) => !t.tags.includes(TokenListTag.WalletOnly) || heldMints.has(t.mint),
        [heldMints]
    );

    const principalTokens = useMemo(() => {
        return tokenList?.filter((d) => !!d.isPrincipal && isListed(d));
    }, [tokenList, isListed]);

    const allCollateralTokens = useMemo(() => {
        return tokenList?.filter((d) => !!d.isCollateral);
    }, [tokenList]);

    const collateralTokens = useMemo(() => {
        return allCollateralTokens?.filter(isListed);
    }, [allCollateralTokens, isListed]);

    const principalMintToToken = useMemo(() => {
        if (!tokenList || isLoading) return undefined;
        return new Map(tokenList.map((t) => [t.mint, t]));
    }, [tokenList, isLoading]);

    const getMetadata = useCallback(
        (mint: string | undefined | null) => {
            if (!mint) return undefined;
            return mintToToken?.get(mint);
        },
        [mintToToken]
    );

    const isPrincipalMint = useCallback(
        (mint: string | undefined | null) => {
            if (!mint || isLoading) return false;
            return !!principalMintToToken?.get(mint)?.isPrincipal;
        },
        [isLoading, principalMintToToken]
    );
    const principalTokenMints = useMemo(() => {
        return principalTokens?.map((t) => t.mint);
    }, [principalTokens]);

    const collateralTokenMints = useMemo(() => {
        return collateralTokens?.map((t) => t.mint);
    }, [collateralTokens]);

    const getDecimals = useCallback(
        (mint: string | undefined) => {
            return getMetadata(mint)?.decimals ?? 0;
        },
        [getMetadata]
    );

    const value = useMemo<TokenListContextValue>(
        () => ({
            principalTokens,
            collateralTokens,
            allCollateralTokens,
            collateralTokenMints,
            principalTokenMints,
            getMetadata,
            getDecimals,
            isPrincipalMint,
            isLoading
        }),
        [
            principalTokens,
            collateralTokens,
            allCollateralTokens,
            collateralTokenMints,
            principalTokenMints,
            getMetadata,
            getDecimals,
            isPrincipalMint,
            isLoading
        ]
    );

    return <TokenListContext.Provider value={value}>{children}</TokenListContext.Provider>;
}

export function useTokenListContext() {
    const context = useContext(TokenListContext);
    if (!context) {
        throw new Error("useTokenListContext must be used within a TokenListProvider");
    }
    return context;
}
